import React, { memo } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import { CUBE_NODE_LAYOUT } from '../utils/elkLayout';

interface AttributeItem {
  id: string;
  handleId: string;
  label: string;
  role: 'INBOUND' | 'OUTBOUND';
  ruleTypeId: string;
  ruleTypeCode: string;
  referenceCode: string;
  sourceLayerName: string | null;
  destinationLayerName: string | null;
}

interface CubeUMLNodeData {
  label: string;
  cubeDefinitionId: string;
  cubeCode: string;
  cubeName: string;
  memberId: string;
  memberName: string | null;
  root: boolean;
  attributes?: AttributeItem[];
}

const nodeStyles: Record<string, React.CSSProperties> = {
  container: {
    background: '#ffffff',
    border: '1.5px solid #334155',
    borderRadius: 4,
    width: CUBE_NODE_LAYOUT.width,
    boxSizing: 'border-box',
    fontFamily: "'Inter', 'Roboto', sans-serif",
    fontSize: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
    overflow: 'visible',
    position: 'relative',
  },
  header: {
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    color: '#ffffff',
    padding: '8px 12px',
    fontWeight: 700,
    fontSize: 12,
    textAlign: 'center' as const,
    letterSpacing: 0.3,
    borderRadius: '3px 3px 0 0',
    minHeight: CUBE_NODE_LAYOUT.headerHeight,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
  },
  stereotype: {
    fontSize: 10,
    fontWeight: 400,
    color: '#94a3b8',
    fontStyle: 'italic',
    marginBottom: 2,
  },
  cubeName: {
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  memberSection: {
    borderTop: '1px solid #e2e8f0',
    padding: '5px 12px',
    background: '#f8fafc',
    fontSize: 11,
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minHeight: CUBE_NODE_LAYOUT.memberSectionHeight,
    boxSizing: 'border-box',
  },
  memberLabel: {
    fontWeight: 600,
    color: '#64748b',
    fontSize: 10,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  memberValue: {
    fontWeight: 500,
    color: '#334155',
    fontSize: 11,
  },
  divider: {
    borderTop: '1.5px solid #334155',
    margin: 0,
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.8,
    padding: '5px 12px 2px',
    background: '#f1f5f9',
    minHeight: CUBE_NODE_LAYOUT.sectionLabelHeight,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
  },
  attributeRow: {
    padding: '3px 12px',
    fontSize: 11,
    color: '#334155',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    lineHeight: 1.4,
    position: 'relative' as const,
    minHeight: CUBE_NODE_LAYOUT.attributeRowHeight,
    boxSizing: 'border-box',
  },
  refCode: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: 10,
    fontWeight: 600,
    color: '#ffffff',
    borderRadius: 3,
    padding: '1px 5px',
    minWidth: 18,
    textAlign: 'center' as const,
    display: 'inline-block',
  },
  refCodeInbound: {
    background: '#2563eb',
  },
  refCodeOutbound: {
    background: '#059669',
  },
  attributeName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  roleIcon: {
    fontSize: 12,
    flexShrink: 0,
  },
  emptyAttributes: {
    padding: '6px 12px',
    fontSize: 11,
    color: '#94a3b8',
    fontStyle: 'italic',
    textAlign: 'center' as const,
    minHeight: CUBE_NODE_LAYOUT.emptyStateHeight,
    boxSizing: 'border-box',
  },
  bottomPad: {
    height: CUBE_NODE_LAYOUT.bottomPad,
  },
};

const handleStyleBase: React.CSSProperties = {
  width: CUBE_NODE_LAYOUT.handleSize,
  height: CUBE_NODE_LAYOUT.handleSize,
  border: '1.5px solid #fff',
};

function CubeUMLNode({ data }: NodeProps<CubeUMLNodeData>) {
  const attributes = data.attributes || [];
  const inboundAttrs = attributes.filter((attribute) => attribute.role === 'INBOUND');
  const outboundAttrs = attributes.filter((attribute) => attribute.role === 'OUTBOUND');

  return (
    <div style={nodeStyles.container}>
      <div style={nodeStyles.header}>
        <div style={nodeStyles.stereotype}>{'<<cube>>'}</div>
        <div style={nodeStyles.cubeName}>
          {data.cubeCode} - {data.cubeName}
        </div>
      </div>

      {data.memberName && (
        <div style={nodeStyles.memberSection}>
          <span style={nodeStyles.memberLabel}>Member:</span>
          <span style={nodeStyles.memberValue}>{data.memberName}</span>
        </div>
      )}

      <div style={nodeStyles.divider} />

      {inboundAttrs.length > 0 && (
        <>
          <div style={nodeStyles.sectionLabel}>v Inbound</div>
          {inboundAttrs.map((attribute) => (
            <div key={attribute.id} style={nodeStyles.attributeRow}>
              <Handle
                type='target'
                position={Position.Left}
                id={attribute.handleId}
                style={{
                  ...handleStyleBase,
                  background: '#2563eb',
                }}
              />
              <span style={{ ...nodeStyles.refCode, ...nodeStyles.refCodeInbound }}>{attribute.referenceCode}</span>
              <span style={nodeStyles.attributeName}>{attribute.label}</span>
              <span style={{ ...nodeStyles.roleIcon, color: '#2563eb' }}>|</span>
            </div>
          ))}
        </>
      )}

      {inboundAttrs.length > 0 && outboundAttrs.length > 0 && (
        <div style={{ borderTop: '1px dashed #cbd5e1', margin: '2px 0' }} />
      )}

      {outboundAttrs.length > 0 && (
        <>
          <div style={nodeStyles.sectionLabel}>^ Outbound</div>
          {outboundAttrs.map((attribute) => (
            <div key={attribute.id} style={nodeStyles.attributeRow}>
              <Handle
                type='source'
                position={Position.Right}
                id={attribute.handleId}
                style={{
                  ...handleStyleBase,
                  background: '#059669',
                }}
              />
              <span style={{ ...nodeStyles.refCode, ...nodeStyles.refCodeOutbound }}>{attribute.referenceCode}</span>
              <span style={nodeStyles.attributeName}>{attribute.label}</span>
              <span style={{ ...nodeStyles.roleIcon, color: '#059669' }}>|</span>
            </div>
          ))}
        </>
      )}

      {attributes.length === 0 && <div style={nodeStyles.emptyAttributes}>No linked attributes</div>}

      <div style={nodeStyles.bottomPad} />
    </div>
  );
}

export default memo(CubeUMLNode);
